/**
 * RoomSwapModal.jsx — IOO-307 Room Swap Feature
 *
 * A self-contained 3-step modal for swapping clinicians between rooms:
 *   Step 1 (SELECT)  — Load swappable-options, show room drag-drop panel
 *   Step 2 (PREVIEW) — Call swap-preview, show affected clinicians + conflicts
 *   Step 3 (CONFIRM) — Call swap-execute, refresh calendar on success
 *
 * Rules enforced:
 *   - GP ↔ GP only, NonGP ↔ NonGP only (type constraint from API)
 *   - Hard conflicts (blocks/lunch) block confirmation
 *   - Spacer conflicts conditionally blocked based on override_spacers flag
 *   - Manual override flag set by backend on execute
 *
 * Props:
 *   visible         {bool}    — controls modal open state
 *   onClose         {func}    — called when modal closes
 *   onSwapSuccess   {func}    — called after successful swap-execute (refresh calendar)
 *   sourceRoom      {object}  — the room the user clicked { room_id, room_name, room_type, rooms[] }
 *   allRooms        {array}   — full appointmentData array (all rooms for the day)
 *   clinicId        {number}  — current clinic ID
 *   selectedDate    {string}  — date string YYYY-MM-DD
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Spin, Tag, Tooltip } from 'antd';
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  CloseOutlined,
  DragOutlined,
  InfoCircleOutlined,
  SwapOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
import { Config } from '../Common/Config';
import './RoomSwapModal.css';

/* ─── Constants ─────────────────────────────────────────── */
const STEP = {
  SELECT: 'SELECT',
  PREVIEW_LOADING: 'PREVIEW_LOADING',
  PREVIEW: 'PREVIEW',
  EXECUTING: 'EXECUTING',
  SUCCESS: 'SUCCESS',
};

const SWAP_TYPE_LABELS = {
  independent_shift: 'Independent Shift',
  paired_swap: 'Paired Swap',
  full_room_swap: 'Full Room Swap',
};

/* ─── Helpers ────────────────────────────────────────────── */
function getInitials(name = '', surname = '') {
  return `${(name[0] || '').toUpperCase()}${(surname[0] || '').toUpperCase()}`;
}

function formatTime(timeStr) {
  if (!timeStr) return '';
  return moment.utc(timeStr).format('HH:mm');
}

function getRoomTypeLabel(roomType) {
  if (!roomType) return '';
  const lower = roomType.toLowerCase();
  if (lower === 'gp') return 'GP';
  return 'Non-GP';
}

/* ─── Axios helper (same pattern as rest of app) ─────────── */
function authHeaders() {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  };
}

/* ─── Sub-components ─────────────────────────────────────── */

/** Single clinician draggable pill */
const ClinicianPill = ({ clinician, isDragging, onDragStart, onDragEnd }) => {
  const name = clinician.clinician_name || clinician.name || '';
  const surname = clinician.clinician_surname || clinician.surname || '';
  const startTime = formatTime(clinician.start || clinician.lantum_start_time);
  const endTime = formatTime(clinician.end || clinician.lantum_end_time);

  return (
    <div
      className={`rsm-clinician-pill${isDragging ? ' rsm-dragging' : ''}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(clinician);
      }}
      onDragEnd={onDragEnd}
      title="Drag to swap room"
    >
      <div className="rsm-clinician-avatar">
        {getInitials(name, surname)}
      </div>
      <div className="rsm-clinician-info">
        <div className="rsm-clinician-name">
          {name} {surname}
        </div>
        {(startTime || endTime) && (
          <div className="rsm-clinician-time">
            {startTime} – {endTime}
          </div>
        )}
      </div>
      <DragOutlined className="rsm-drag-handle" />
    </div>
  );
};

/** Room card (source / eligible / ineligible / selected-target) */
const RoomCard = ({
  room,
  variant,          // 'source' | 'eligible' | 'ineligible' | 'selected-target'
  swapType,         // from swappable-options API
  isSource,
  draggingClinician,
  onDragStart,
  onDragEnd,
  onDropOnRoom,
  onClickRoom,
  isDragOver,
  onDragOverRoom,
  onDragLeaveRoom,
}) => {
  /* Collect clinicians from the room's rooms[] array (lantum type only) */
  const clinicians = [];
  if (room.rooms && Array.isArray(room.rooms)) {
    room.rooms.forEach((r) => {
      if (r.type === 'lantum' && r.clinicians && r.clinicians.length > 0) {
        r.clinicians.forEach((c) => {
          clinicians.push({
            ...c,
            // Prefer clinician-level availability_id (unique per clinician slot).
            // Fall back to room-level r.availability_id only when the clinician
            // object itself doesn't carry one.
            availability_id: c.availability_id || r.availability_id || r.id || null,
            room_id: room.room_id,
            room_name: room.room_name,
          });
        });
      }
    });
  }

  const cardClass = [
    'rsm-room-card',
    variant === 'source' ? 'rsm-source' : '',
    variant === 'eligible' ? 'rsm-eligible' : '',
    variant === 'ineligible' ? 'rsm-ineligible' : '',
    variant === 'selected-target' ? 'rsm-selected-target' : '',
    isDragOver && variant !== 'ineligible' ? 'rsm-drag-over' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const isDroppable = variant === 'eligible' || variant === 'selected-target';
  const roomTypeLabel = getRoomTypeLabel(room.room_type);

  return (
    <Tooltip
      title={
        variant === 'ineligible'
          ? `Cannot swap: different room type (${roomTypeLabel})`
          : variant === 'source'
            ? 'Source room drag a clinician to another room'
            : 'Drop here to swap'
      }
      placement="top"
      color="#1a1a1a"
    >
      <div
        className={cardClass}
        onClick={() => isDroppable && onClickRoom(room, swapType)}
        onDragOver={(e) => {
          if (isDroppable) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            onDragOverRoom(room.room_id);
          }
        }}
        onDragLeave={() => onDragLeaveRoom()}
        onDrop={(e) => {
          e.preventDefault();
          if (isDroppable && draggingClinician) {
            onDropOnRoom(room, swapType);
          }
        }}
      >
        {/* Header */}
        <div className="rsm-room-card-header">
          <div>
            {isSource && <span className="rsm-source-label">📍 Source</span>}
            <div className="rsm-room-name">{room.room_name}</div>
          </div>
          <span className={`rsm-room-type-badge ${roomTypeLabel === 'GP' ? 'gp' : 'non-gp'}`}>
            {roomTypeLabel || 'Room'}
          </span>
        </div>

        {/* Swap type badge (only on eligible/selected) */}
        {swapType && variant !== 'ineligible' && variant !== 'source' && (
          <span className={`rsm-swap-type-badge ${swapType}`}>
            <SwapOutlined style={{ marginRight: 4 }} />
            {SWAP_TYPE_LABELS[swapType] || swapType}
          </span>
        )}

        {/* Clinicians */}
        <div className="rsm-clinicians-list">
          {clinicians.length === 0 ? (
            <div className="rsm-no-clinician">No clinicians</div>
          ) : (
            clinicians.map((c, idx) => (
              <ClinicianPill
                key={`${c.clinician_id || idx}`}
                clinician={c}
                isDragging={
                  draggingClinician &&
                  (draggingClinician.clinician_id === c.clinician_id)
                }
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
              />
            ))
          )}
        </div>
      </div>
    </Tooltip>
  );
};

/** Animated SVG swap arrow between two room card elements */
const SwapArrowSVG = ({ sourceRef, targetRef, containerRef }) => {
  const [coords, setCoords] = useState(null);

  useEffect(() => {
    if (!sourceRef?.current || !targetRef?.current || !containerRef?.current) return;

    const updateCoords = () => {
      const containerRect = containerRef.current.getBoundingClientRect();
      const srcRect = sourceRef.current.getBoundingClientRect();
      const tgtRect = targetRef.current.getBoundingClientRect();

      const srcCenterX = srcRect.left + srcRect.width / 2 - containerRect.left;
      const srcCenterY = srcRect.bottom - containerRect.top;
      const tgtCenterX = tgtRect.left + tgtRect.width / 2 - containerRect.left;
      const tgtCenterY = tgtRect.top - containerRect.top;

      setCoords({
        x1: srcCenterX, y1: srcCenterY,
        x2: tgtCenterX, y2: tgtCenterY,
      });
    };

    updateCoords();
    window.addEventListener('resize', updateCoords);
    return () => window.removeEventListener('resize', updateCoords);
  }, [sourceRef, targetRef, containerRef]);

  if (!coords) return null;

  const { x1, y1, x2, y2 } = coords;
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;

  return (
    <svg
      className="rsm-arrow-container"
      style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', zIndex: 10 }}
    >
      <defs>
        <marker
          id="arrowhead-fwd"
          markerWidth="8"
          markerHeight="8"
          refX="6"
          refY="3"
          orient="auto"
        >
          <polygon points="0 0, 8 3, 0 6" fill="#52c41a" />
        </marker>
        <marker
          id="arrowhead-bwd"
          markerWidth="8"
          markerHeight="8"
          refX="2"
          refY="3"
          orient="auto"
        >
          <polygon points="8 0, 0 3, 8 6" fill="#008385" />
        </marker>
      </defs>

      {/* Forward arrow: source → target */}
      <path
        d={`M ${x1 - 8},${y1} C ${x1 - 8},${midY} ${x2 - 8},${midY} ${x2 - 8},${y2}`}
        fill="none"
        stroke="#52c41a"
        strokeWidth="2"
        strokeDasharray="8 4"
        markerEnd="url(#arrowhead-fwd)"
        className="rsm-swap-arrow"
      />

      {/* Backward arrow: target → source (paired swap) */}
      <path
        d={`M ${x2 + 8},${y2} C ${x2 + 8},${midY} ${x1 + 8},${midY} ${x1 + 8},${y1}`}
        fill="none"
        stroke="#008385"
        strokeWidth="2"
        strokeDasharray="8 4"
        markerEnd="url(#arrowhead-bwd)"
        className="rsm-swap-arrow"
        style={{ animationDelay: '0.3s' }}
      />
    </svg>
  );
};

/* ─── Main Component ─────────────────────────────────────── */
const RoomSwapModal = ({
  visible,
  onClose,
  onSwapSuccess,
  sourceRoom,
  allRooms,
  clinicId,
  selectedDate,
}) => {
  // ── State ──────────────────────────────────────────────
  const [step, setStep] = useState(STEP.SELECT);
  const [swappableOptions, setSwappableOptions] = useState([]); // from API
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(null);
  const [optionsApiMessage, setOptionsApiMessage] = useState(null); // message from API when no options

  const [draggingClinician, setDraggingClinician] = useState(null);
  const [dragOverRoomId, setDragOverRoomId] = useState(null);
  // Ref to hold the availability_id of the currently-dragged clinician synchronously
  // (avoids stale-closure issues with setSelectedAvailabilityId being async)
  const draggingAvailabilityIdRef = useRef(null);

  const [selectedTarget, setSelectedTarget] = useState(null); // { room, swapType }
  const [selectedAvailabilityId, setSelectedAvailabilityId] = useState(null);

  const [previewData, setPreviewData] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  const [executeError, setExecuteError] = useState(null);

  // Arrow refs
  const roomsRowRef = useRef(null);
  const sourceCardRef = useRef(null);
  const targetCardRef = useRef(null);

  // ── Reset on open ──────────────────────────────────────
  useEffect(() => {
    if (visible) {
      setStep(STEP.SELECT);
      setSwappableOptions([]);
      setOptionsLoading(false);
      setOptionsError(null);
      setOptionsApiMessage(null);
      setDraggingClinician(null);
      setDragOverRoomId(null);
      setSelectedTarget(null);
      setSelectedAvailabilityId(null);
      setPreviewData(null);
      setPreviewError(null);
      setExecuteError(null);
    }
  }, [visible]);

  // ── Load swappable options on open ────────────────────
  useEffect(() => {
    if (!visible || !sourceRoom) return;

    const firstAvailabilityId = getFirstAvailabilityId(sourceRoom);
    if (!firstAvailabilityId) {
      setOptionsError('No clinician availability found in this room for today.');
      return;
    }
    // Set default selected availability to the first clinician
    setSelectedAvailabilityId(firstAvailabilityId);

    // Collect ALL availability IDs from every clinician in the source room
    // and send them in one request so the backend returns eligible rooms
    // for all clinicians at once — no need to re-call on drag.
    const allIds = getAllAvailabilityIds(sourceRoom);
    fetchSwappableOptions(allIds.length > 0 ? allIds : [firstAvailabilityId]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, sourceRoom]);

  // ── Helpers ────────────────────────────────────────────
  function getFirstAvailabilityId(room) {
    if (!room || !room.rooms) return null;
    for (const r of room.rooms) {
      if (r.type === 'lantum') {
        // availability_id is often stored directly on the lantum room object
        if (r.availability_id) return r.availability_id;
        if (r.id) return r.id;
        // fallback: check clinicians array
        if (r.clinicians && r.clinicians.length > 0) {
          const c = r.clinicians[0];
          if (c.availability_id) return c.availability_id;
        }
      }
    }
    return null;
  }

  /** Collects ALL unique availability_ids from every clinician in the source room.
   *  Used to pass them all at once to swappable-options so the backend returns
   *  eligible rooms for every clinician in a single request. */
  function getAllAvailabilityIds(room) {
    const ids = new Set();
    if (!room || !room.rooms) return [];
    room.rooms.forEach((r) => {
      if (r.type !== 'lantum') return;
      // clinician-level id takes priority (unique per slot)
      if (r.clinicians && r.clinicians.length > 0) {
        r.clinicians.forEach((c) => {
          const id = c.availability_id || r.availability_id || r.id;
          if (id) ids.add(id);
        });
      } else {
        const id = r.availability_id || r.id;
        if (id) ids.add(id);
      }
    });
    return Array.from(ids);
  }

  function getAvailabilityIdForClinician(clinician) {
    // The clinician pill injects availability_id from the parent lantum room
    return clinician.availability_id || selectedAvailabilityId;
  }

  // ── API Calls ──────────────────────────────────────────

  /** @param {number|number[]} availabilityIds — single id or array of ids */
  const fetchSwappableOptions = async (availabilityIds) => {
    setOptionsLoading(true);
    setOptionsError(null);
    setOptionsApiMessage(null);
    try {
      const idsArray = Array.isArray(availabilityIds) ? availabilityIds : [availabilityIds];

      // GET with repeated query params: ?clinic_id=40&date=...&availability_ids[]=105&availability_ids[]=106
      const params = new URLSearchParams();
      params.append('clinic_id', clinicId);
      params.append('date', selectedDate);
      idsArray.forEach((id) => params.append('availability_ids[]', id));

      const res = await axios.get(`${Config.url}rooms/swappable-options`, {
        params,
        headers: authHeaders(),
      });

      if (res?.data?.success && Array.isArray(res.data.data)) {
        setSwappableOptions(res.data.data);
        // Store API message even on success — shown when result list is empty
        if (res.data.message) setOptionsApiMessage(res.data.message);
      } else {
        setOptionsApiMessage(res?.data?.message || null);
        setOptionsError(res?.data?.message || 'Failed to load swappable options.');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || 'Failed to load swappable options.';
      setOptionsError(msg);
    } finally {
      setOptionsLoading(false);
    }
  };

  const fetchSwapPreview = useCallback(
    async (targetRoomId, overrideAvailabilityId) => {
      // Prefer the explicitly-passed id (set synchronously on drag), then fall back to state
      const availabilityId = overrideAvailabilityId ?? selectedAvailabilityId;
      if (!availabilityId || !sourceRoom?.room_id || !targetRoomId) return;

      setStep(STEP.PREVIEW_LOADING);
      setPreviewData(null);
      setPreviewError(null);

      try {
        const res = await axios.post(
          `${Config.url}rooms/swap-preview`,
          {
            clinic_id: clinicId,
            source_room_id: sourceRoom.room_id,
            target_room_id: targetRoomId,
            availability_id: availabilityId,
            date: selectedDate,
          },
          { headers: authHeaders() }
        );

        if (res?.data?.success) {
          setPreviewData(res.data.data);
          setStep(STEP.PREVIEW);
        } else {
          setPreviewError(res?.data?.message || 'Conflict detected. Swap cannot proceed.');
          setStep(STEP.PREVIEW);
        }
      } catch (err) {
        const msg =
          err?.response?.data?.message || err?.message || 'Preview failed.';
        setPreviewError(msg);
        setStep(STEP.PREVIEW);
      }
    },
    [clinicId, selectedAvailabilityId, selectedDate, sourceRoom]
  );

  const executeSwap = async () => {
    if (!selectedTarget) return;
    setStep(STEP.EXECUTING);
    setExecuteError(null);

    try {
      const res = await axios.post(
        `${Config.url}rooms/swap-execute`,
        {
          clinic_id: clinicId,
          source_room_id: sourceRoom.room_id,
          target_room_id: selectedTarget.room.room_id,
          availability_id: selectedAvailabilityId,
          date: selectedDate,
        },
        { headers: authHeaders() }
      );

      if (res?.data?.success) {
        setStep(STEP.SUCCESS);
        setTimeout(() => {
          onClose();
          onSwapSuccess();
        }, 2000);
      } else {
        setExecuteError(res?.data?.message || 'Swap execution failed.');
        setStep(STEP.PREVIEW);
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Swap execution failed.';
      setExecuteError(msg);
      setStep(STEP.PREVIEW);
    }
  };

  // ── Drag & Drop handlers ───────────────────────────────
  const handleDragStart = (clinician) => {
    setDraggingClinician(clinician);
    // Resolve this clinician's availability_id synchronously into the ref
    // so handleDropOnRoom can read it without relying on async state.
    const aid = clinician.availability_id || selectedAvailabilityId;
    draggingAvailabilityIdRef.current = aid || null;
    // Keep selectedAvailabilityId in sync for click-based selections
    if (aid && aid !== selectedAvailabilityId) {
      setSelectedAvailabilityId(aid);
      // No need to re-fetch swappable-options here: all availability IDs
      // were sent to the backend in a single call when the modal opened.
    }
  };

  const handleDragEnd = () => {
    setDraggingClinician(null);
    setDragOverRoomId(null);
    draggingAvailabilityIdRef.current = null;
  };

  const handleDropOnRoom = (targetRoom, swapType) => {
    // Capture the ref value BEFORE clearing state (it gets nulled in handleDragEnd)
    const availabilityIdForDrop = draggingAvailabilityIdRef.current;
    setDraggingClinician(null);
    setDragOverRoomId(null);
    draggingAvailabilityIdRef.current = null;
    selectTargetRoom(targetRoom, swapType, availabilityIdForDrop);
  };

  const handleClickRoom = (targetRoom, swapType) => {
    selectTargetRoom(targetRoom, swapType);
  };

  const selectTargetRoom = (targetRoom, swapType, availabilityId) => {
    if (availabilityId) setSelectedAvailabilityId(availabilityId);
    setSelectedTarget({ room: targetRoom, swapType });
    fetchSwapPreview(targetRoom.room_id, availabilityId);
  };

  // ── Derive room variants ───────────────────────────────

  /** Returns the swappable-options entry for a given room that matches
   *  the currently active clinician's availability_id.
   *  Falls back to any entry for that room if no clinician-specific match. */
  function getOptionForRoom(room) {
    // Active availability_id: prefer the dragging clinician's id, else state
    const activeId = draggingClinician?.availability_id || selectedAvailabilityId;

    // 1. Exact match: same room AND same clinician
    if (activeId) {
      const exact = swappableOptions.find(
        (o) => o.room_id === room.room_id && o.availability_id === activeId
      );
      if (exact) return exact;
    }

    // 2. Fallback: any entry for this room (single-clinician rooms)
    return swappableOptions.find((o) => o.room_id === room.room_id) || null;
  }

  function getRoomVariant(room) {
    if (room.room_id === sourceRoom?.room_id) return 'source';
    const option = getOptionForRoom(room);
    if (option) {
      return selectedTarget?.room?.room_id === room.room_id
        ? 'selected-target'
        : 'eligible';
    }
    return 'ineligible';
  }

  function getSwapTypeForRoom(room) {
    // Only show swap_type badge while a clinician is being dragged
    // OR after a target room has already been selected.
    if (!draggingClinician && !selectedTarget) return null;
    return getOptionForRoom(room)?.swap_type || null;
  }

  // ── Step dots ──────────────────────────────────────────
  const stepDots = [
    { key: STEP.SELECT, label: 'Select' },
    { key: STEP.PREVIEW, label: 'Preview' },
    { key: STEP.SUCCESS, label: 'Done' },
  ];

  const currentStepIndex =
    step === STEP.SELECT || step === STEP.PREVIEW_LOADING
      ? 0
      : step === STEP.PREVIEW || step === STEP.EXECUTING
        ? 1
        : 2;

  // ── Render helpers ─────────────────────────────────────
  const renderRoomsPanel = () => {
    if (optionsLoading) {
      return (
        <div className="rsm-loading-wrap">
          <Spin className="custom-spinner" />
          <span className="rsm-loading-text">Loading swappable rooms…</span>
        </div>
      );
    }

    if (optionsError) {
      return (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message="Cannot load options"
          description={optionsError}
          className="rsm-conflict-alert"
          style={{ margin: '16px 24px' }}
        />
      );
    }

    // IOO-307 rule: GP ↔ GP only, NonGP ↔ NonGP only.
    // Normalise both to lowercase and strip hyphens so "GP", "gp", "Non-GP", "nongp" all match.
    const normaliseType = (t = '') => t.toLowerCase().replace(/[-_\s]/g, '');
    const sourceType = normaliseType(sourceRoom?.room_type);

    // Only show rooms of the same type as the source (plus the source room itself).
    // Different-type rooms are completely hidden — not shown as ineligible.
    // Source room is always sorted first.
    const rooms = (allRooms || [])
      .filter((room) => {
        const roomType = normaliseType(room.room_type);
        if (room.room_id === sourceRoom?.room_id) return true;
        if (sourceType && roomType && roomType !== sourceType) return false;
        return true;
      })
      .sort((a, b) => {
        // Source room always first
        if (a.room_id === sourceRoom?.room_id) return -1;
        if (b.room_id === sourceRoom?.room_id) return 1;
        return 0;
      });

    return (
      <div className="rsm-rooms-panel">
        <div className="rsm-rooms-title">
          <SwapOutlined style={{ marginRight: 6 }} />
          Rooms — Drag a clinician to swap
        </div>

        <div className="rsm-rooms-row" ref={roomsRowRef}>
          {rooms.map((room) => {
            const variant = getRoomVariant(room);
            const swapType = getSwapTypeForRoom(room);
            const isSource = room.room_id === sourceRoom?.room_id;

            return (
              <div
                key={room.room_id}
                ref={isSource ? sourceCardRef : selectedTarget?.room?.room_id === room.room_id ? targetCardRef : null}
                style={{ display: 'flex' }}
              >
                <RoomCard
                  room={room}
                  variant={variant}
                  swapType={swapType}
                  isSource={isSource}
                  draggingClinician={draggingClinician}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDropOnRoom={handleDropOnRoom}
                  onClickRoom={handleClickRoom}
                  isDragOver={dragOverRoomId === room.room_id}
                  onDragOverRoom={(roomId) => setDragOverRoomId(roomId)}
                  onDragLeaveRoom={() => setDragOverRoomId(null)}
                />
              </div>
            );
          })}

          {/* SVG arrow overlay between source and selected target */}
          {selectedTarget && sourceCardRef.current && targetCardRef.current && (
            <SwapArrowSVG
              sourceRef={sourceCardRef}
              targetRef={targetCardRef}
              containerRef={roomsRowRef}
            />
          )}
        </div>

        {optionsLoading === false && swappableOptions.length === 0 && !optionsError && (
          <Alert
            type="info"
            showIcon
            message={optionsApiMessage || 'No eligible rooms found for swapping.'}
            description={
              optionsApiMessage
                ? null
                : 'There are no rooms of the same type available for a swap today.'
            }
            style={{ marginTop: 12 }}
          />
        )}
      </div>
    );
  };

  const renderPreviewSection = () => {
    if (step === STEP.PREVIEW_LOADING || step === STEP.EXECUTING) {
      return (
        <div className="rsm-loading-wrap">
          <Spin className="custom-spinner" />
          <span className="rsm-loading-text">
            {step === STEP.EXECUTING ? 'Executing swap…' : 'Generating preview…'}
          </span>
        </div>
      );
    }

    if (step !== STEP.PREVIEW && step !== STEP.PREVIEW_LOADING) return null;

    return (
      <div className="rsm-preview-section">
        <div className="rsm-preview-title">
          <InfoCircleOutlined />
          Swap Preview
          {selectedTarget && (
            <Tag color="cyan" style={{ marginLeft: 8, fontWeight: 600 }}>
              {sourceRoom?.room_name} → {selectedTarget.room.room_name}
            </Tag>
          )}
        </div>

        {/* Execute error from previous attempt */}
        {executeError && (
          <Alert
            type="error"
            showIcon
            message="Swap failed"
            description={executeError}
            className="rsm-conflict-alert"
            style={{ marginBottom: 14 }}
          />
        )}

        {/* Conflict error from preview */}
        {previewError && (
          <Alert
            type="error"
            showIcon
            icon={<WarningOutlined />}
            message="Conflict detected — swap blocked"
            description={previewError}
            className="rsm-conflict-alert"
          />
        )}

        {previewData && (
          <>
            {/* Summary stats */}
            <div className="rsm-preview-summary">
              <div className="rsm-preview-stat">
                <div className="rsm-preview-stat-value">
                  {previewData.affected_availabilities?.length || 0}
                </div>
                <div className="rsm-preview-stat-label">Clinicians Moving</div>
              </div>
              {/* <div className="rsm-preview-stat">
                <div className="rsm-preview-stat-value">
                  {previewData.total_appointments_moved || 0}
                </div>
                <div className="rsm-preview-stat-label">Appointments Moving</div>
              </div> */}
              {previewData.swap_type && (
                <div className="rsm-preview-stat">
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#008385', marginTop: 4 }}>
                    {SWAP_TYPE_LABELS[previewData.swap_type] || previewData.swap_type}
                  </div>
                  <div className="rsm-preview-stat-label">Swap Type</div>
                </div>
              )}
            </div>

            {/* Affected availabilities list */}
            {previewData.affected_availabilities?.length > 0 && (
              <div className="rsm-preview-body">
                <div className="rsm-affected-list">
                  {previewData.affected_availabilities.map((av, idx) => {
                    const name = av.clinician_name || '';
                    const initials = name
                      .split(' ')
                      .map((w) => w[0] || '')
                      .join('')
                      .toUpperCase()
                      .slice(0, 2);
                    const startFmt = av.start_time
                      ? moment.utc(av.start_time).format('HH:mm')
                      : '';
                    const endFmt = av.end_time
                      ? moment.utc(av.end_time).format('HH:mm')
                      : '';

                    return (
                      <div key={av.availability_id || idx} className="rsm-affected-item">
                        <div className="rsm-affected-avatar">{initials}</div>
                        <div className="rsm-affected-details">
                          <div className="rsm-affected-name">{name}</div>
                          {(startFmt || endFmt) && (
                            <div className="rsm-affected-time">
                              {startFmt} – {endFmt}
                            </div>
                          )}
                        </div>
                        <div className="rsm-affected-move">
                          <span className="rsm-room-from">
                            {av.moving_from?.room_name || `Room ${av.moving_from}`}
                          </span>
                          <ArrowRightOutlined className="rsm-move-arrow-icon" />
                          <span className="rsm-room-to">
                            {av.moving_to?.room_name || `Room ${av.moving_to}`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  const renderSuccess = () => (
    <div className="rsm-success-state">
      <div className="rsm-success-icon">
        <CheckCircleFilled style={{ fontSize: 36, color: '#fff' }} />
      </div>
      <div className="rsm-success-title">Room Swap Complete!</div>
      <div className="rsm-success-subtitle">
        The calendar will refresh automatically…
      </div>
    </div>
  );

  // ── Footer buttons ─────────────────────────────────────
  const renderFooter = () => {
    if (step === STEP.SUCCESS) return null;
    if (step === STEP.EXECUTING || step === STEP.PREVIEW_LOADING) {
      return (
        <div className="rsm-footer">
          <Button className="rsm-btn-cancel" disabled>
            Cancel
          </Button>
          <Button className="rsm-btn-confirm" loading disabled>
            {step === STEP.EXECUTING ? 'Executing…' : 'Loading…'}
          </Button>
        </div>
      );
    }

    if (step === STEP.PREVIEW) {
      const isConflict = !!previewError;
      return (
        <div className="rsm-footer">
          <Button
            className="rsm-btn-cancel"
            onClick={() => {
              setStep(STEP.SELECT);
              setSelectedTarget(null);
              setPreviewData(null);
              setPreviewError(null);
              setExecuteError(null);
            }}
          >
            ← Back
          </Button>
          <Tooltip
            title={isConflict ? 'Resolve conflicts before confirming' : ''}
          >
            <Button
              className="rsm-btn-confirm"
              disabled={isConflict || !previewData}
              onClick={executeSwap}
            >
              Confirm Swap
            </Button>
          </Tooltip>
        </div>
      );
    }

    // SELECT step footer
    return (
      <div className="rsm-footer">
        <Button className="rsm-btn-cancel" onClick={onClose}>
          Cancel
        </Button>
        <Tooltip title={!selectedTarget ? 'Drag or click a target room first' : ''}>
          <Button
            className="rsm-btn-preview"
            disabled={!selectedTarget}
            onClick={() => fetchSwapPreview(selectedTarget?.room?.room_id)}
          >
            Preview Swap →
          </Button>
        </Tooltip>
      </div>
    );
  };

  // ── Modal title — format: "Room 1 GP Swap" ─────────────
  const modalTitle = (() => {
    const roomName = sourceRoom?.room_name || '';
    const roomType = sourceRoom?.room_type ? sourceRoom.room_type.toUpperCase() : '';
    const label = roomName
      ? `${roomName}${roomType ? ' ' + roomType : ''} Swap`
      : 'Room Swap';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <SwapOutlined style={{ fontSize: 18, color: '#fff' }} />
        <span>{label}</span>
      </div>
    );
  })();

  // ── Render ─────────────────────────────────────────────
  return (
    <Modal
      open={visible}
      onCancel={onClose}
      title={modalTitle}
      footer={null}
      width={860}
      className="room-swap-modal"
      destroyOnClose
      closeIcon={
        <CloseOutlined className='room-swap-close-icon' style={{ color: 'rgba(255,255,255,0.85)', fontSize: 16, background: '#008385' }} />
      }
    >
      {/* Step indicator banner */}
      <div className="rsm-instruction-banner">
        <div className="rsm-step-indicator">
          {stepDots.map((dot, idx) => (
            <div
              key={dot.key}
              className={`rsm-step-dot${idx === currentStepIndex ? ' active' : idx < currentStepIndex ? ' done' : ''}`}
            />
          ))}
        </div>
        <span>
          {step === STEP.SELECT || step === STEP.PREVIEW_LOADING
            ? '① Select a target room by dragging a clinician or clicking an eligible room'
            : step === STEP.PREVIEW || step === STEP.EXECUTING
              ? '② Review affected clinicians and appointments, then confirm'
              : '③ Swap complete — calendar refreshing'}
        </span>
      </div>

      {/* Main content */}
      {step === STEP.SUCCESS ? (
        renderSuccess()
      ) : (
        <>
          {/* Always show the rooms panel in SELECT step */}
          {(step === STEP.SELECT || step === STEP.PREVIEW_LOADING) && renderRoomsPanel()}

          {/* In PREVIEW or EXECUTING: show condensed source→target info above preview */}
          {(step === STEP.PREVIEW || step === STEP.EXECUTING || step === STEP.PREVIEW_LOADING) && (
            <div className="rsm-rooms-panel" style={{ paddingBottom: 8 }}>
              <div className="rsm-rooms-title">
                <SwapOutlined style={{ marginRight: 6 }} />
                Swap Direction
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <div style={{
                  background: '#e6f5f5',
                  border: '2px solid #008385',
                  borderRadius: 10,
                  padding: '8px 14px',
                  fontWeight: 600,
                  fontSize: 13,
                  color: '#006567',
                }}>
                  📍 {sourceRoom?.room_name}
                </div>
                <ArrowRightOutlined style={{ color: '#008385', fontSize: 18 }} />
                <div style={{
                  background: '#f6ffed',
                  border: '2px solid #52c41a',
                  borderRadius: 10,
                  padding: '8px 14px',
                  fontWeight: 600,
                  fontSize: 13,
                  color: '#389e0d',
                }}>
                  🎯 {selectedTarget?.room?.room_name}
                </div>
                {selectedTarget?.swapType && (
                  <span className={`rsm-swap-type-badge ${selectedTarget.swapType}`}>
                    {SWAP_TYPE_LABELS[selectedTarget.swapType] || selectedTarget.swapType}
                  </span>
                )}
              </div>
            </div>
          )}

          {renderPreviewSection()}
        </>
      )}

      {renderFooter()}
    </Modal>
  );
};

export default RoomSwapModal;
